import { useCallback, useEffect, useRef } from "react";

const DEFAULT_DEBOUNCE_MS = 300;

interface UseDebouncedSearchOptions<T> {
  term?: string;
  enabled?: boolean;
  fetcher: (term: string, signal: AbortSignal) => Promise<T[]>;
  setItems: (items: T[]) => void;
  setLoading: (loading: boolean) => void;
  isValid?: (term: string) => boolean;
  onError?: (error: unknown) => void;
  debounceMs?: number;
}

export function useDebouncedSearch<T>({
  term,
  enabled = true,
  fetcher,
  setItems,
  setLoading,
  isValid,
  onError,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UseDebouncedSearchOptions<T>) {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const fetcherRef = useRef(fetcher);
  const isValidRef = useRef(isValid);
  const setItemsRef = useRef(setItems);
  const setLoadingRef = useRef(setLoading);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    fetcherRef.current = fetcher;
    isValidRef.current = isValid;
    setItemsRef.current = setItems;
    setLoadingRef.current = setLoading;
    onErrorRef.current = onError;
  });

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      controllerRef.current?.abort();
    };
  }, []);

  const trigger = useCallback(
    (termArg: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      controllerRef.current?.abort();

      const check = isValidRef.current;
      const valid = check ? check(termArg) : termArg.trim().length > 0;
      if (!valid) {
        setItemsRef.current([]);
        setLoadingRef.current(false);
        return;
      }

      setLoadingRef.current(true);
      debounceRef.current = setTimeout(async () => {
        const controller = new AbortController();
        controllerRef.current = controller;
        try {
          const items = await fetcherRef.current(termArg, controller.signal);
          if (controller.signal.aborted) return;
          setItemsRef.current(items);
        } catch (error) {
          if (controller.signal.aborted) return;
          onErrorRef.current?.(error);
        } finally {
          if (!controller.signal.aborted) setLoadingRef.current(false);
        }
      }, debounceMs);
    },
    [debounceMs],
  );

  useEffect(() => {
    if (term === undefined) return;
    if (!enabled) {
      setItemsRef.current([]);
      setLoadingRef.current(false);
      return;
    }
    trigger(term);
  }, [term, enabled, trigger]);

  return trigger;
}
